import {
  Alert,
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Center,
  Code,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconActivity, IconAlertCircle, IconChevronRight, IconRefresh } from '@tabler/icons';
import { Link } from 'react-router-dom';
import { DetailRow, StatusBadge } from '../../../components';
import { JOB_POLL_INTERVAL_MS } from '../../../magic-values';
import { useAccount } from '../../../queries/use-accounts';
import { isJobActive, useJob } from '../../../queries/use-jobs';
import { formatDate } from './format-date';
import { JobProgressCell } from './job-progress-cell';
import { JobTasksSection } from './job-tasks-section';
import { JsonSection } from './json-section';

interface JobViewProps {
  accountId: string;
  jobId: string;
}

export function JobView({ accountId, jobId }: JobViewProps) {
  const { data: account } = useAccount(accountId);
  const { data: job, isLoading, isFetching, isError, error, refetch } = useJob(jobId, accountId);

  return (
    <Stack spacing="xl">
      <Breadcrumbs separator={<IconChevronRight size={12} />}>
        <Anchor component={Link} to="/accounts" color="dimmed" size="sm">
          Accounts
        </Anchor>
        <Anchor component={Link} to={`/account/${accountId}`} color="dimmed" size="sm">
          {account?.name ?? accountId}
        </Anchor>
        <Anchor component={Link} to={`/account/${accountId}/jobs`} color="dimmed" size="sm">
          Jobs
        </Anchor>
        <Text size="sm">{job?.type ?? jobId}</Text>
      </Breadcrumbs>

      {job ? (
        <>
          <Box>
            <Group position="apart" align="flex-start" noWrap>
              <Group spacing="md" align="center" noWrap>
                <ThemeIcon size={42} radius="md" variant="light" color="bagel">
                  <IconActivity size={22} />
                </ThemeIcon>
                <Box>
                  <Group spacing="sm" align="center">
                    <Title order={2}>{job.type}</Title>
                    <StatusBadge status={job.status} />
                  </Group>
                  <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
                    {job._id}
                  </Text>
                </Box>
              </Group>
              <Button
                leftIcon={<IconRefresh size={16} />}
                variant="subtle"
                color="bagel"
                size="sm"
                onClick={() => refetch()}
                loading={isFetching}
              >
                Refresh
              </Button>
            </Group>
          </Box>

          <Paper withBorder p="lg">
            <Group spacing="xl" align="flex-start">
              <DetailRow
                label="Origin"
                value={
                  <Text size="sm" sx={{ fontFamily: 'monospace' }}>
                    {job.origin || '—'}
                  </Text>
                }
              />
              <DetailRow label="Trigger" value={job.trigger ?? '—'} />
              <DetailRow
                label="Initiated by"
                value={job.initiatedBy?.label ?? job.initiatedBy?.kind ?? '—'}
              />
              <DetailRow label="Created" value={formatDate(job.createdAt)} />
              <DetailRow label="Started" value={formatDate(job.startedAt)} />
              <DetailRow label="Finished" value={formatDate(job.finishedAt)} />
            </Group>
          </Paper>

          <Paper withBorder p="lg">
            <Group spacing="xl" align="flex-start">
              <DetailRow label="Total" value={job.progress.total ?? 'streaming'} />
              <DetailRow label="Succeeded" value={job.progress.succeeded} />
              <DetailRow label="Failed" value={job.progress.failed} />
              <DetailRow label="Cancelled" value={job.progress.cancelled} />
              <DetailRow label="Created tasks" value={job.createdTaskCount} />
              <DetailRow label="Progress" value={<JobProgressCell progress={job.progress} />} />
            </Group>
          </Paper>

          {job.error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title={job.error.code}>
              <Stack spacing="xs">
                <Text size="sm">{job.error.message}</Text>
                {job.error.details && (
                  <Code block sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {JSON.stringify(job.error.details, null, 2)}
                  </Code>
                )}
              </Stack>
            </Alert>
          )}

          <JsonSection title="Params" data={job.params} />
          <JsonSection title="Metadata" data={job.metadata} />

          <Stack spacing="sm">
            <Title order={4}>Tasks</Title>
            <JobTasksSection
              accountId={accountId}
              jobId={jobId}
              refetchInterval={isJobActive(job.status) ? JOB_POLL_INTERVAL_MS : false}
            />
          </Stack>
        </>
      ) : isLoading ? (
        <Center mih={200}>
          <Loader />
        </Center>
      ) : isError ? (
        <Text color="red">Failed to load job: {(error as Error)?.message ?? 'Unknown error'}</Text>
      ) : (
        <Text color="dimmed">Job not found.</Text>
      )}
    </Stack>
  );
}
